import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CalendarDays, Loader2 } from "lucide-react";
import { AgendaWeekGrid, AgendaDayList } from "../components/agenda/AgendaViews";
import { ViewToggle, WeekNav, addDays, fmtLong, defaultView } from "../components/agenda/AgendaControls";
import { Counters } from "../components/agenda/Counters";

export default function MiaAgenda() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super;
  const [week, setWeek] = useState("");
  const [squadraId, setSquadraId] = useState("");
  const [squadre, setSquadre] = useState([]);
  const [view, setView] = useState(() => defaultView("agenda_view"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agenda/settimana", { params: { week_start: week, squadra_id: squadraId } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, [week, squadraId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) api.get("/squadre").then((r) => setSquadre(r.data)).catch(() => {}); }, [isAdmin]);

  const changeView = (v) => { setView(v); localStorage.setItem("agenda_view", v); };
  const items = data?.items || [];

  return (
    <div data-testid="mia-agenda-page" className="space-y-6">
      <div className="flex flex-wrap items-end gap-4 justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <CalendarDays className="w-7 h-7" style={{ color: "var(--primary)" }} /> La mia agenda
          </h1>
          <p className="text-sm mt-1 flex items-center gap-2" style={{ color: "var(--text-muted)" }} data-testid="agenda-squadra-label">
            {data?.squadra ? (<><span className="w-2.5 h-2.5 rounded-full" style={{ background: data.squadra.color }} /> Squadra <b>{data.squadra.name}</b></>) : "Nessuna squadra assegnata"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <select value={squadraId || data?.squadra?.id || ""} onChange={(e) => setSquadraId(e.target.value)} data-testid="agenda-squadra-select"
              className="px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }}>
              {squadre.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <ViewToggle view={view} onChange={changeView} />
        </div>
      </div>

      <WeekNav disabled={!data} label={data && `${fmtLong(data.week_start)} – ${fmtLong(data.week_end)}`}
        onPrev={() => setWeek(addDays(data.week_start, -7))} onNext={() => setWeek(addDays(data.week_start, 7))} onToday={() => setWeek("")}>
        <Counters items={items} />
      </WeekNav>

      {loading && !data ? (
        <div className="text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
      ) : !data?.squadra ? (
        <div className="card-shell p-8 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="agenda-no-squadra">
          Non sei ancora responsabile o supervisore di nessuna squadra. Chiedi all'amministratore di assegnartene una dalla pagina Utenti.
        </div>
      ) : view === "settimana" ? <AgendaWeekGrid days={data.days} items={items} /> : <AgendaDayList days={data.days} items={items} />}
    </div>
  );
}
